import { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import type { UserInfo } from '@kidstube/types';
import { apiFetch } from '@kidstube/lib/api';

interface LayoutProps {
  user: UserInfo | null;
  children: React.ReactNode;
}

export default function Layout({ user, children }: LayoutProps) {
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    }

    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [menuOpen]);

  const handleSignOut = async () => {
    localStorage.clear();
    sessionStorage.clear();
    try {
      await apiFetch('/api/auth/logout', { method: 'POST' });
    } finally {
      window.location.href = '/';
    }
  };

  return (
    <div className="app-layout">
      <header className="app-header">
        <Link to="/" className="logo">
          <img src="/icons/logo.svg" alt="" className="logo-icon" />
          <span>KidsTube</span>
        </Link>

        <nav className="header-nav">
          <Link
            to="/"
            className={`chip-btn ${location.pathname === '/' ? 'chip-btn-active' : 'chip-btn-ghost'}`}
          >
            Home
          </Link>

          {user?.isParent && (
            <Link
              to="/parent"
              className={`chip-btn ${location.pathname.startsWith('/parent') ? 'chip-btn-active' : 'chip-btn-ghost'}`}
            >
              Parent
            </Link>
          )}

          {user && (
            <div className="user-menu-container" ref={menuRef}>
              <button 
                className="user-avatar" 
                onClick={() => setMenuOpen(!menuOpen)}
                aria-expanded={menuOpen}
                aria-haspopup="true"
              >
                {user.picture ? (
                  <img src={user.picture} alt={user.name || user.email} />
                ) : (
                  user.name?.[0] || user.email[0].toUpperCase()
                )}
              </button>
              
              {menuOpen && (
                <div className="user-menu">
                  <div className="user-menu-header">
                  <div className="user-menu-avatar">
                    {user.picture ? (
                      <img src={user.picture} alt={user.name || user.email} />
                    ) : (
                      user.name?.[0] || user.email[0].toUpperCase()
                    )}
                  </div>
                    <div className="user-menu-info">
                      {user.name && <div className="user-menu-name">{user.name}</div>}
                      <div className="user-menu-email">{user.email}</div>
                    </div>
                  </div>
                  <div className="user-menu-divider" />
                  <button 
                    className="user-menu-item"
                    onClick={handleSignOut}
                  >
                    Sign out
                  </button>
                </div>
              )}
            </div>
          )}
        </nav>
      </header>

      <main className="main-content">{children}</main>
    </div>
  );
}
